import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Chip, Divider, Table, TableBody, TableRow, TableCell, CircularProgress
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { FormsApi, RISK_COLOR, fmtDateTime } from "../formsUtils";
import { exportResponsePdf } from "./responsePdf";
import { laudoDaTriagem } from "./triagemDigital";

function formatValue(v) {
  if (v === null || v === undefined) return "-";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") {
    if (v.foreign) return `${v.doc || "-"}${v.nationality ? " (" + v.nationality + ")" : ""}`;
    return JSON.stringify(v);
  }
  return String(v);
}

/** Bloco de escore configurável (presentation genérica do scoringEngine). */
function ScoringBlock({ presentation }) {
  const { headline = [], items = [], lists = [] } = presentation || {};
  return (
    <Box sx={{ mb: 2, p: 2, border: "1px solid #eee", borderRadius: 1, background: "#fafafa" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Escore / Avaliação de risco</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        {headline.map((h, i) => {
          const color = RISK_COLOR[h.value];
          return (
            <Chip key={i} label={`${h.label}: ${formatValue(h.value)}`}
              sx={color ? { bgcolor: color, color: "#fff" } : undefined}
              variant={color ? "filled" : "outlined"} />
          );
        })}
      </Box>
      {items.length > 0 && (
        <Table size="small">
          <TableBody>
            {items.map((it, i) => (
              <TableRow key={i}>
                <TableCell sx={{ fontWeight: 500, width: "45%" }}>{it.label}</TableCell>
                <TableCell>{formatValue(it.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {lists.map((lst, i) => (
        (lst.entries && lst.entries.length > 0) ? (
          <Box key={i} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{lst.title}:</Typography>
            {lst.entries.map((e, j) => (
              <Typography key={j} variant="body2">• {e.label}{e.value !== undefined && e.value !== null && e.value !== "" ? ` — ${formatValue(e.value)}` : ""}</Typography>
            ))}
          </Box>
        ) : null
      ))}
    </Box>
  );
}

/** Bloco de risco clínico (escore APA legado). */
function ClinicalBlock({ clinical }) {
  const presentByCategory = (cat) =>
    Object.entries(clinical.categories?.[cat] || {})
      .filter(([, v]) => v["Presente?"])
      .map(([name, v]) => ({ name, risk: v.Risco }));

  const categories = ["Cardio-Circulatório", "Pulmonar", "Endócrino", "Outras Condições"];

  // Resposta sem cálculo de risco (formulário fora do APA, ou resposta antiga) chegava aqui
  // e o bloco imprimia "Risco do paciente: undefined" no insumo do anestesista.
  if (!clinical.patient_risk && !clinical.asa && !clinical.categories) return null;

  return (
    <Box sx={{ mb: 2, p: 2, border: "1px solid #eee", borderRadius: 1, background: "#fafafa" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Avaliação de risco (APA)</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        {clinical.patient_risk && (
          <Chip label={`Risco do paciente: ${clinical.patient_risk}`}
            sx={{ bgcolor: RISK_COLOR[clinical.patient_risk] || "#777", color: "#fff" }} />
        )}
        {clinical.asa && <Chip label={`Classificação ${clinical.asa}`} variant="outlined" />}
        {clinical.age?.value != null && <Chip size="small" variant="outlined" label={`Idade: ${clinical.age.value} (${clinical.age.risk})`} />}
        {clinical.imc?.value != null && <Chip size="small" variant="outlined" label={`IMC: ${clinical.imc.value} - ${clinical.imc.classification}`} />}
        {clinical.pregnancy && <Chip size="small" color="warning" label="Possibilidade de gravidez" />}
      </Box>

      {categories.map((cat) => {
        const present = presentByCategory(cat);
        if (present.length === 0) return null;
        return (
          <Box key={cat} sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{cat}:</Typography>{" "}
            {present.map((p) => (
              <Chip key={p.name} size="small" sx={{ mr: 0.5, mb: 0.5, borderColor: RISK_COLOR[p.risk], color: RISK_COLOR[p.risk] }}
                variant="outlined" label={`${p.name} (${p.risk})`} />
            ))}
          </Box>
        );
      })}

      {clinical.medications && clinical.medications.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Medicamentos / suspensão:</Typography>
          {clinical.medications.map((m, i) => (
            <Typography key={i} variant="body2">
              • {m.Medicamento} — {m["Suspensão?"] ? `suspender ${m["Intervalo (dias)"]} dia(s) antes` : "verificar conduta"}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Linha({ rotulo, valor }) {
  return <Typography variant="body2"><strong>{rotulo}:</strong> {valor || "-"}</Typography>;
}

function RiscoChip({ risco }) {
  return (
    <Chip size="small" variant="outlined" label={risco || "-"}
      sx={{ borderColor: RISK_COLOR[risco], color: RISK_COLOR[risco] }} />
  );
}

function Grupo({ titulo, children }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>{titulo}</Typography>
      {children}
    </Box>
  );
}

/** Laudo "Triagem Eletrônica": o mesmo conteúdo do PDF que o robô manda para o D2I. */
function LaudoBlock({ laudo, patient }) {
  return (
    <Box sx={{ mb: 2, p: 2, border: "1px solid #eee", borderRadius: 1, background: "#fafafa" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Triagem eletrônica</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Chip label={`Risco do paciente: ${laudo.riscoPaciente}`}
          sx={{ bgcolor: RISK_COLOR[laudo.riscoPaciente] || "#777", color: "#fff" }} />
        {laudo.asa && <Chip label={`Classificação: ${laudo.asa}`} variant="outlined" />}
      </Box>

      <Grupo titulo="Dados gerais">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3 }}>
          <Linha rotulo="Nome completo" valor={patient.name} />
          <Linha rotulo="CPF" valor={patient.external_ref} />
          <Linha rotulo="Sexo ao nascimento" valor={laudo.sexo} />
          <Linha rotulo="Altura/Peso" valor={laudo.alturaPeso} />
          <Linha rotulo="Idade" valor={laudo.idade} />
          <Linha rotulo="Telefone" valor={laudo.telefone} />
          <Linha rotulo="Cirurgião" valor={laudo.cirurgiao} />
        </Box>
      </Grupo>

      <Grupo titulo="Condições gerais">
        <Linha rotulo="Gravidez" valor={laudo.gravidez} />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Linha rotulo="IMC" valor={laudo.imc} /> <RiscoChip risco={laudo.riscoImc} />
        </Box>
      </Grupo>

      <Grupo titulo="Hábitos sociais">
        <Linha rotulo="Etilismo" valor={laudo.habitos.etilismo} />
        <Linha rotulo="Tabagismo" valor={`${laudo.habitos.tabagismo} | Período: ${laudo.habitos.periodo || "-"}`} />
        <Linha rotulo="Outras drogas" valor={laudo.habitos.drogas} />
      </Grupo>

      {laudo.grupos.map((g) => (
        <Grupo key={g.titulo} titulo={g.titulo}>
          {g.itens.length === 0 && <Typography variant="body2" color="text.secondary">Nenhuma</Typography>}
          {g.itens.map((c) => (
            <Box key={c.nome} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
              <Typography variant="body2">{c.nome}</Typography> <RiscoChip risco={c.risco} />
            </Box>
          ))}
        </Grupo>
      ))}

      <Grupo titulo="Medicações em uso">
        {laudo.medicamentos.length === 0 && <Typography variant="body2" color="text.secondary">Nenhuma</Typography>}
        {laudo.medicamentos.map((m, i) => (
          <Typography key={i} variant="body2">• {m.nome} — suspensão: {m.suspensao}</Typography>
        ))}
      </Grupo>

      <Grupo titulo="Alergias anteriores">
        {laudo.alergias.length === 0 && <Typography variant="body2" color="text.secondary">Nenhuma</Typography>}
        {laudo.alergias.map((a, i) => <Typography key={i} variant="body2">• {a}</Typography>)}
      </Grupo>

      <Grupo titulo="Histórico de cirurgias anteriores">
        {laudo.tiposAnestesia.length === 0 && <Typography variant="body2" color="text.secondary">Nenhum</Typography>}
        {laudo.tiposAnestesia.map((a, i) => <Typography key={i} variant="body2">• {a}</Typography>)}
      </Grupo>

      <Grupo titulo="Ansiedade/desejo da informação">
        <Linha rotulo="Sentimentos sobre a anestesia" valor={laudo.sentimentos} />
        <Linha rotulo="Desejo de detalhes" valor={laudo.desejoDetalhes} />
        <Linha rotulo="Desejo de consulta" valor={laudo.desejoConsulta} />
      </Grupo>

      <Grupo titulo="Estratificação de risco">
        {laudo.estratificacao.map((e) => (
          <Box key={e.nome} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2" sx={{ minWidth: 240 }}>{e.nome}</Typography> <RiscoChip risco={e.valor} />
          </Box>
        ))}
      </Grupo>
    </Box>
  );
}

/** Detalhe de uma resposta + insumo consolidado para o médico. */
export default function ResponseDetail({ open, onClose, formId, responseId }) {
  const [loading, setLoading] = useState(true);
  const [insumo, setInsumo] = useState(null);

  useEffect(() => {
    if (!open || !responseId) return;
    setLoading(true);
    // Zera antes de buscar: sem isto, uma busca que falha deixa na tela o insumo do
    // paciente anterior como se fosse o desta resposta.
    setInsumo(null);
    FormsApi.getInsumo(formId, responseId).then(
      (resp) => { setInsumo(resp.data); setLoading(false); },
      () => setLoading(false)
    );
  }, [open, formId, responseId]);

  const laudo = laudoDaTriagem(insumo && insumo.clinical);

  function handlePrint() {
    if (insumo) exportResponsePdf(insumo);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resposta — insumo para avaliação</DialogTitle>
      <DialogContent dividers id="insumo-print">
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
        ) : !insumo ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Não foi possível carregar os dados desta resposta.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6">{insumo.form_title}</Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1, mb: 2 }}>
              <Typography variant="body2"><strong>Paciente:</strong> {insumo.patient.name || "-"}</Typography>
              <Typography variant="body2"><strong>Telefone:</strong> {insumo.patient.phone || "-"}</Typography>
              <Typography variant="body2"><strong>Enviado:</strong> {fmtDateTime(insumo.submitted_at)}</Typography>
            </Box>

            {insumo.scores && insumo.scores.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Escores</Typography>
                {insumo.scores.map((s) => (
                  <Chip key={s.name} sx={{ mr: 1, mt: 0.5 }} color="primary"
                    label={`${s.name}: ${s.value}`} />
                ))}
              </Box>
            )}

            {insumo.clinical && insumo.clinical.presentation && <ScoringBlock presentation={insumo.clinical.presentation} />}
            {laudo && <LaudoBlock laudo={laudo} patient={insumo.patient} />}
            {insumo.clinical && !insumo.clinical.presentation && !laudo && <ClinicalBlock clinical={insumo.clinical} />}

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Respostas</Typography>
            <Table size="small">
              <TableBody>
                {insumo.items.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: "45%", fontWeight: 500, verticalAlign: "top" }}>{item.label}</TableCell>
                    <TableCell>{formatValue(item.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<PrintIcon />} onClick={handlePrint}>Imprimir / PDF</Button>
        <Button variant="contained" onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
